import React, { useState, useEffect } from "react";
import APIPlansContainer from "./APIPlans";
import APIKeysContainer from "./APIKeysContainer";
import StakingStatsContainer from "./StakingStats";
import { Layout } from "../components/Layout";
import McpInfoContainer from "./McpInfoContainer";
import PrepaidKeysContainer from "./PrepaidKeysContainer";
import { hasPrepaidKeys as checkHasPrepaidKeys } from "../helpers/prepaidKeyManager";
import CreatePrepaidKeyModal from "./modals/CreatePrepaidKeyModal";
import PrepaidKeyCreatedModal from "./modals/PrepaidKeyCreatedModal";
import RefillPrepaidKeyModal from "./modals/RefillPrepaidKeyModal";
import PrepaidKeyRefilledModal from "./modals/PrepaidKeyRefilledModal";
import { env, STRIPE_CUSTOMER_PORTAL, useGlobalState } from "../helpers/GlobalStateProvider";
import { openPortalOnClick } from "../helpers/stripePortal";
import { TIER_CREDITS } from "../helpers/tierConstants";
import { getPlanForTier, planTiers } from "./APIPlans/planData";
import { getApiUrl } from "../config";
import type { PaymentRecord } from "./APIPlans/types";
import { getToken } from "../helpers/tokenManager";
import {
  Box,
  Card,
  Flex,
  Link,
  Spinner,
  Table,
  Text,
  Stack,
} from "@chakra-ui/react";
import { Button } from "../components/ui/button";
import { LuArrowRight, LuArrowUpRight } from "react-icons/lu";

// ─── Consolidated Subscription Status Section ────────────────────────────────
const SubscriptionStatusSection: React.FC = () => {
  const { info, bearerToken } = useGlobalState();

  // Pick the best available tier: paid Stripe subscription > staked tier > legacy tier field.
  // Using explicit `> 0` checks because `??` treats 0 as a valid value and would short-circuit
  // incorrectly when a user has no Stripe subscription (stripeTier === 0) but is a staking subscriber.
  const effectiveTier =
    info?.stripeTier && info.stripeTier > 0
      ? info.stripeTier
      : info?.stakedTier && info.stakedTier > 0
        ? info.stakedTier
        : (info?.tier ?? 0);
  const planName = getPlanForTier(effectiveTier);
  const totalCredits = info?.credits ?? TIER_CREDITS[effectiveTier] ?? 500;
  const usedCredits =
    info?.apiKeys?.reduce(
      (total: number, key: any) => total + (key?.monthly_usage || 0),
      0
    ) || 0;

  const paymentMethod = (info as any)?.paymentMethod || null;
  const billingInterval = (info as any)?.billingInterval || null;
  const paidThrough = info?.paidThrough || null;
  const trialEnd = (info as any)?.trialEnd || null;
  const nextPaymentDateField = (info as any)?.nextPaymentDate || null;
  const nextPaymentAmountField = (info as any)?.nextPaymentAmount;

  // Fetch payment history
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const response = await fetch(
          `${getApiUrl(env)}/v3/apiKey/payment_history`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: "{}",
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        setPaymentHistory(data?.payments || []);
      } catch {
        // Silently fail
      }
    };
    fetchPaymentHistory();
  }, [bearerToken]);

  // ── Derived values ────────────────────────────────────────────────────────
  // Show "MOR Staking" for users whose tier is staking-derived (no Stripe
  // payment on file) so the payment-method cell reflects how they're paying
  // for their plan instead of being a blank em-dash.
  const isStakingSubscriber =
    !paymentMethod && (info?.stakedTier ?? 0) > 0;
  const paymentMethodLabel = paymentMethod
    ? "Credit card"
    : isStakingSubscriber
      ? "MOR Staking"
      : "—";

  // Trial detection: prefer backend `trialEnd` field; fall back to heuristic
  // when backend hasn't shipped the field yet. Stripe creates a $0 placeholder
  // payment row when a subscription enters trial, so we filter to "real"
  // (amount > 0) payments before deciding the user has actually been charged.
  const realPayments = paymentHistory.filter((p) => (p.amount ?? 0) > 0);
  const heuristicInTrial =
    !!info?.tier && info.tier > 0 &&
    realPayments.length === 0 &&
    !!paidThrough &&
    new Date(paidThrough).getTime() > Date.now();
  const isInTrial = trialEnd
    ? new Date(trialEnd).getTime() > Date.now()
    : heuristicInTrial;

  // Next payment date: trial end if in trial, else backend nextPaymentDate, else paidThrough
  const nextPaymentDateRaw =
    (trialEnd && new Date(trialEnd).getTime() > Date.now())
      ? trialEnd
      : (nextPaymentDateField || paidThrough);
  const formattedNextPaymentDate = nextPaymentDateRaw
    ? new Date(nextPaymentDateRaw).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  // Trial end label for the "Free Trial · ends MMM D" badge
  const formatTrialEnd = trialEnd
    ? new Date(trialEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : paidThrough
      ? new Date(paidThrough).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : "";

  // Next payment amount: backend value (cents) preferred, else derive from plan tier
  const planForTier = planTiers.find(
    (p) => p.monthlyTier === effectiveTier || p.annualTier === effectiveTier,
  );
  const fallbackAmountDollars = planForTier
    ? billingInterval === "year"
      ? planForTier.annualBilled
      : planForTier.monthlyPrice
    : undefined;
  const nextPaymentAmount =
    typeof nextPaymentAmountField === "number"
      ? nextPaymentAmountField / 100
      : fallbackAmountDollars;

  return (
    <Stack gap={4}>
      {/* Main status card */}
      <Card.Root>
        <Card.Header pb={0}>
          <Card.Title
            fontSize="30px"
            fontWeight="bold"
            letterSpacing="-0.4px"
            lineHeight="32px"
          >
            Subscription
          </Card.Title>
        </Card.Header>
        <Card.Body pt={4}>
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap={6}
          >
            {/* Left side: Plan details */}
            <Flex flex={1} direction="column" gap={3}>
              {/* Badge row: Plan, Frequency, optional Free Trial */}
              <Flex alignItems="center" gap={2} flexWrap="wrap">
                <Flex
                  bg="#f3e8ff"
                  borderRadius="6px"
                  px="12px"
                  py="4px"
                  alignItems="center"
                >
                  <Text fontSize="sm" fontWeight="bold" color="#7c3aed">
                    {planName} Plan
                  </Text>
                </Flex>
                <Flex
                  bg="#f4f4f5"
                  borderRadius="6px"
                  px="12px"
                  py="4px"
                  alignItems="center"
                >
                  <Text fontSize="sm" fontWeight="bold" color="#52525b">
                    {billingInterval === "year" ? "Annual" : "Monthly"}
                  </Text>
                </Flex>
                {isInTrial && (
                  <Flex
                    bg="#dbeafe"
                    borderRadius="6px"
                    px="12px"
                    py="4px"
                    alignItems="center"
                  >
                    <Text fontSize="sm" fontWeight="bold" color="#1d4ed8">
                      Free Trial{formatTrialEnd ? ` \u00b7 ends ${formatTrialEnd}` : ""}
                    </Text>
                  </Flex>
                )}
              </Flex>

              {/* Stacked label/value fields \u2014 uppercase section labels above values */}
              <Stack gap={4} mt={2}>
                <Stack gap="2px">
                  <Text
                    fontSize="11px"
                    fontWeight="semibold"
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                    color="#a1a1aa"
                  >
                    Next payment
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" color="#18181b">
                    {nextPaymentAmount != null
                      ? `$${nextPaymentAmount.toFixed(2)}`
                      : "\u2014"}
                    {nextPaymentDateRaw && (
                      <Text as="span" color="#52525b" fontWeight="normal">
                        {" "}{"\u00b7"} {formattedNextPaymentDate}
                      </Text>
                    )}
                  </Text>
                </Stack>

                <Stack gap="2px">
                  <Text
                    fontSize="11px"
                    fontWeight="semibold"
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                    color="#a1a1aa"
                  >
                    Payment method
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" color="#18181b">
                    {paymentMethodLabel}
                  </Text>
                </Stack>
              </Stack>

              {/* Manage subscription link */}
              <Box mt={1}>
                <Button size="sm" variant="outline" asChild>
                  <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                    Manage Subscription <LuArrowUpRight />
                  </Link>
                </Button>
              </Box>
            </Flex>

            {/* Vertical divider (desktop) / horizontal divider (mobile) */}
            <Box
              display={{ base: "none", lg: "block" }}
              w="1px"
              bg="#e4e4e7"
              alignSelf="stretch"
            />
            <Box
              display={{ base: "block", lg: "none" }}
              h="1px"
              bg="#e4e4e7"
              w="full"
            />

            {/* Right side: Payment History (compact) */}
            <Flex flex={1} direction="column" gap={2}>
              <Text fontSize="sm" fontWeight="semibold" color="#18181b">
                Payment History
              </Text>
              {paymentHistory.length > 0 ? (
                <>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Date</Table.ColumnHeader>
                        <Table.ColumnHeader>Description</Table.ColumnHeader>
                        <Table.ColumnHeader>Amount</Table.ColumnHeader>
                        <Table.ColumnHeader></Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {paymentHistory.slice(0, 3).map((payment, idx) => {
                        const isPlanChange = payment.planChange === true;
                        const isTrialActivation = payment.amount === 0 && !isPlanChange;
                        const description = isPlanChange
                          ? `Plan changed to ${payment.tierName || "new plan"}`
                          : isTrialActivation
                            ? `Trial started${payment.tierName ? ` \u2014 ${payment.tierName}` : ""}`
                            : payment.tierName ||
                              "Subscription";
                        return (
                          <Table.Row
                            key={payment.invoiceNumber || idx}
                          >
                            <Table.Cell>
                              {new Date(payment.date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </Table.Cell>
                            <Table.Cell>
                              <Text fontSize="sm" color={isTrialActivation || isPlanChange ? "#52525b" : "#18181b"}>
                                {description}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              {isTrialActivation || (isPlanChange && payment.amount === 0)
                                ? <Text fontSize="sm" color="#a1a1aa">{"\u2014"}</Text>
                                : `$${(payment.amount / 100).toFixed(2)}`}
                            </Table.Cell>
                            <Table.Cell>
                              {payment.invoiceUrl && (() => {
                                const isValidInvoiceUrl =
                                  typeof payment.invoiceUrl === "string" &&
                                  payment.invoiceUrl.startsWith("https://") &&
                                  /^https:\/\/([a-z0-9-]+\.)*stripe\.com(\/|$)/i.test(payment.invoiceUrl);
                                return isValidInvoiceUrl ? (
                                  <Link href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer">
                                    <LuArrowUpRight />
                                  </Link>
                                ) : (
                                  <Text fontSize="xs" color="fg.muted">Invoice</Text>
                                );
                              })()}
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                  <Flex justifyContent="flex-end" mt={1}>
                    <Button size="xs" variant="ghost" asChild>
                      <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                        View full billing history <LuArrowUpRight />
                      </Link>
                    </Button>
                  </Flex>
                </>
              ) : (
                <Text fontSize="xs" color="#a1a1aa">No payments yet.</Text>
              )}
            </Flex>
          </Flex>
        </Card.Body>
      </Card.Root>

      {/* Credits usage is now inside APIKeysContainer */}
    </Stack>
  );
};

// ─── Dashboard ───────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { info, isSubscriber, isSignedIn } = useGlobalState();
  const subscriber = isSubscriber();

  // Only show a loading spinner for *authenticated* users whose `/v3/apiKey/info`
  // response has not come back yet. Unauthenticated visitors never populate
  // `info`, so we treat them as "loaded" and render the prospect view directly.
  const isLoading = isSignedIn() && info === null;

  // Track whether user has prepaid keys (for conditional rendering in subscriber view)
  const [hasPrepaidKeys, setHasPrepaidKeys] = useState(
    () => checkHasPrepaidKeys()
  );

  useEffect(() => {
    const handleUpdate = () =>
      setHasPrepaidKeys(checkHasPrepaidKeys());
    window.addEventListener("storage", handleUpdate);
    window.addEventListener("prepaidKeysUpdated", handleUpdate);
    return () => {
      window.removeEventListener("storage", handleUpdate);
      window.removeEventListener("prepaidKeysUpdated", handleUpdate);
    };
  }, []);

  if (isLoading) {
    return (
      <Layout behavior="dashboard" showFooter>
        <Flex justify="center" align="center" minH="50vh" w="100%">
          <Spinner size="xl" color="purple.500" />
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout behavior="dashboard" showFooter>
      {/* MOR Staking always renders at the top of the dashboard so its
          position is consistent regardless of account state (subscriber vs
          prospect). Everything below is gated on whether the user has an
          active subscription. */}
      <StakingStatsContainer />
      {subscriber ? (
        <>
          {/* Subscriber layout (below staking):
              1. Subscription details + payment history (side by side)
              2. My API Keys (with credits usage bar in footer)
              3. Developer Tools & Resources (with prepaid info folded in)
              4. Prepaid Keys table (only if user has prepaid keys)
          */}
          <SubscriptionStatusSection />
          <APIKeysContainer />
          <McpInfoContainer />
          {hasPrepaidKeys && <PrepaidKeysContainer />}
        </>
      ) : (
        <>
          {/* Prospect layout (below staking): API keys -> prepaid -> MCP -> plans */}
          <APIKeysContainer />
          <PrepaidKeysContainer />
          <McpInfoContainer />
          <APIPlansContainer />
        </>
      )}

      {/* Prepaid Key Modals */}
      <CreatePrepaidKeyModal />
      <PrepaidKeyCreatedModal />
      <RefillPrepaidKeyModal />
      <PrepaidKeyRefilledModal />

    </Layout>
  );
};

export default Dashboard;
